import { CheckBox } from '@design/components/checkbox/CheckBox';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { SmartTable, SmartTableColumn } from '@design/components/smart-table';
import { formatDateToLocale, Formats } from '@helpers';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useDocumentsEntity } from '@terminal/react-query-hooks/documents/useDocumentsEntity';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import earningsStyles from '../earnings/Earnings.module.scss';
import styles from './DocumentWidget.module.scss';
import { useDocumentWidgetStore } from './useDocumentWidgetStore';
import { DocumentTableItem, groupDocumentByYear, isFirstItem } from './utils';

export interface DocumentProps {
  entityTag: string;
}

export const Document = ({ entityTag }: DocumentProps) => {
  const { t } = useTranslation(['widget', 'asset-overview', 'chat', 'common']);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const {
    data: documents,
    isError,
    isFetching,
    error,
  } = useDocumentsEntity(entityTag);

  const {
    initStore,
    getIsRowSelected,
    updateRowSelection,
    toggleAllRowsSelection,
    getIsAllRowsSelected,
    getIsSomeRowsSelected,
  } = useDocumentWidgetStore(state => ({
    initStore: state.initStore,
    rowSelection: state.rowSelection,
    getIsRowSelected: state.getIsRowSelected,
    updateRowSelection: state.updateRowSelection,
    toggleAllRowsSelection: state.toggleAllRowsSelection,
    getIsAllRowsSelected: state.getIsAllRowsSelected,
    getIsSomeRowsSelected: state.getIsSomeRowsSelected,
  }));

  useEffect(() => {
    if (!isFetching && !!documents?.length) {
      initStore(documents);
    }
  }, [isFetching, documents]);

  const docsGroupedByYear = groupDocumentByYear(documents);

  if (isError) {
    throw new Error(error.message);
  }

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              maxWidth: '1280px',
              borderRadius: '8px',
              height: '360px',
              margin: '20px auto',
            },
          },
        ]}
        data-testid="document-widget-loader"
      />
    );
  }

  if (!documents?.length) {
    return (
      <ErrorMessageWrapper
        className={styles.styledErrorWrapper}
        displayIcon
        variant="empty"
      />
    );
  }

  const columns: SmartTableColumn<DocumentTableItem>[] = [
    {
      label: '',
      key: 'id',
      renderHeader: () => (
        <span className={earningsStyles.tableHeaderContent}>
          <CheckBox
            name="all-documents"
            label=""
            onChange={(_, e) => toggleAllRowsSelection(e.target.checked)}
            checked={getIsAllRowsSelected() || getIsSomeRowsSelected()}
            partial={getIsSomeRowsSelected() && !getIsAllRowsSelected()}
          />
        </span>
      ),
      render: props => {
        const id = props;
        return (
          <div className={earningsStyles.wrapperCheckbox}>
            <CheckBox
              className={styles.styledCheckBox}
              name={`document-${id}`}
              label=""
              checked={getIsRowSelected(id)}
              onChange={() => updateRowSelection(id, !getIsRowSelected(id))}
            />
          </div>
        );
      },
    },
    {
      label: t('widget:assetOverview.tableDocuments.year'),
      key: 'year',
      renderCell: props => (
        <SmartTable.TableCell
          key={props.column.key}
          className={clsx(styles.cell, {
            [styles.withBorder]: true,
          })}
          {...props}
        />
      ),
      render: (value, item) =>
        value !== 0 && isFirstItem(docsGroupedByYear, item) ? (
          <span className={styles.numericalValue}>{value}</span>
        ) : (
          <span />
        ),
    },
    {
      label: t('widget:assetOverview.tableDocuments.calendar'),
      key: 'period',
      render: value => <span className={styles.numericalValue}>{value}</span>,
    },
    {
      label: t('widget:assetOverview.tableDocuments.filingDate'),
      key: 'release_date',
      render: (_, document) => {
        const date = new Date(document.release_date);
        const formatTime = formatDateToLocale(
          languageSetting,
          date,
          Formats['yyyy-MM-dd']
        );
        return <span className={styles.numericalValue}>{formatTime}</span>;
      },
    },
    {
      label: t('widget:assetOverview.tableDocuments.DocumentType'),
      key: 'kind',
    },
    {
      label: t('widget:assetOverview.tableDocuments.companyFiscal'),
      key: 'reference',
    },
    {
      label: t('widget:assetOverview.tableDocuments.file'),
      key: 'name',
      render: (_, document) => (
        <a className={styles.pdfLink} href={document.location} target="_blank" rel="noreferrer">
          <span className={styles.pdfWrapper}>
            <img
              className={styles.pdfIcon}
              src="/app/static/images/icons/pdf.svg"
            />
            {document.name}
          </span>
        </a>
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <SmartTable
        className={styles.styledSmartTable}
        isHeaderSticky
        columns={columns}
        data={documents}
        renderActionColumn={() => <></>}
      />
    </div>
  );
};
